import { parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

import type { CartLine } from './cart-math'

/**
 * Adding to, changing and removing from the cart.
 *
 * Every one of these is addressed by **line index**, not by product id. Legacy
 * addresses them by product id, and the cart really can hold two lines with the
 * same id — the same pizza with different toppings, or the same drink ordered
 * in two kitchen rounds. Matching by id changes both (FB-053).
 *
 * `CartItemsList` in legacy already takes a `lineIndex` prop with a comment
 * explaining exactly this; `new-order.tsx` passes it and then ignores it.
 */

export interface OrderCartLine extends CartLine {
  isPizza?: boolean
  pizzaExtras?: string
  /** The surcharge already folded into `unitPrice`, so an edit can undo it. */
  extrasPriceCents?: number
  /**
   * Which toppings, and how many portions of each — §10A.80.
   *
   * `pizzaExtras` above is the *text*, which is what the kitchen ticket reads and what legacy
   * re-parses to reopen the dialog. This is what the line is **charged** from: the server prices
   * each ingredient from `pizza_ingredients` at the line's size, because §10A.9 re-reads every
   * catalogue price and would otherwise throw the surcharge away — which is exactly what it did,
   * silently, from the day §9.41 shipped.
   */
  extrasChosen?: ReadonlyArray<{ ingredientId: number; quantity: number }>
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  lunchMealType?: string
  /**
   * A line from the day's menu — §9.178, drawn as one box in the cart.
   *
   * **Client-only, and deliberately not `boxGroupId`.** That field travels to the server, is stored
   * on `order_items`, and means *remove as a unit*: a burger box is assembled as one and comes apart
   * as one. A day's menu is not — each meal was added on its own and has to be removable on its own.
   */
  lunchGroup?: boolean
  /** A line from an earlier kitchen round; shown with a note about what changing it does. */
  locked?: boolean
  /**
   * The server's id for a line that came off an existing order - §9.374.
   *
   * `order-edit.ts` has always set it; it lives on the type now because **`addProduct` reads it**:
   * merging an addition into a line the kitchen already has a ticket for hid the addition from the
   * edit payload, which is FB-668's *"Zatiaľ nič nepridané"*.
   */
  existingId?: number
  /**
   * What a composed line is assembled from — `BACKEND.md` §10A.14. A kombo's three pizzas, a custom
   * pizza's size, base and toppings. Absent on an ordinary catalogue line, which is most of them.
   */
  parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design
   * and the sum of three pizzas is far too loose a cap. Absent everywhere else, and a line without
   * it is bounded exactly as it was before.
   */
  composition?: 'kombo'
}

export interface AddableProduct {
  id: number
  name: string
  price: MoneyInput
  categoryName?: string
  /** §9.85 — which of the product's sizes, priced by `BACKEND.md` §10A.33. */
  size?: string
  /**
   * §10A.14, for a composed line. A merge keeps the line already in the cart, which is right: two
   * lines only merge when they share an id, and a composed id is a hash of the line's own name — so
   * their parts are the same parts.
   */
  parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design
   * and the sum of three pizzas is far too loose a cap. Absent everywhere else, and a line without
   * it is bounded exactly as it was before.
   */
  composition?: 'kombo'
}

/**
 * Adds one, merging into an existing line only when that line is genuinely the
 * same thing: same product, no box group, no toppings, not locked.
 *
 * A pizza that has been customised is its own line — merging it would silently
 * apply one customer's toppings to another's pizza.
 */
export function addProduct(
  cart: OrderCartLine[],
  product: AddableProduct,
  { isPizza = false }: { isPizza?: boolean } = {},
): OrderCartLine[] {
  // §9.85 — the size is part of *the same thing*. A 32 cm and a 50 cm of one pizza are two lines at
  // two prices; merging them would charge one of the two wrongly, whichever line won.
  // §9.374, FB-668 - **never into a line that came from the server.** The owner: *"when in previous
  // round they had kofola in košík, and I added new one, I received «Zatiaľ nič nepridané»."* A line
  // with an `existingId` is one the kitchen already has a ticket for; merging into it raised the
  // quantity on a line the edit payload did not look at, so the addition vanished and the guest
  // drank a kofola nobody was charged for. As its own line it travels as `newItems` and the bar gets
  // a round for it - which is better than legacy, where the merge makes it a silent correction.
  const mergeableIndex = cart.findIndex(
    (line) =>
      line.productId === product.id &&
      line.size === product.size &&
      !line.boxGroupId &&
      !line.pizzaExtras &&
      !line.locked &&
      line.existingId === undefined,
  )

  if (mergeableIndex >= 0) {
    return cart.map((line, index) =>
      index === mergeableIndex ? { ...line, quantity: line.quantity + 1 } : line,
    )
  }

  return [
    ...cart,
    {
      ...(product.size === undefined ? {} : { size: product.size }),
      productId: product.id,
      productName: product.name,
      unitPrice: product.price,
      quantity: 1,
      ...(isPizza ? { isPizza: true } : {}),
      ...(product.categoryName ? { categoryName: product.categoryName } : {}),
      ...(product.parts && product.parts.length > 0 ? { parts: product.parts } : {}),
      // §10A.26 — carried through so the kombo's `max` bound survives the basket.
      ...(product.composition ? { composition: product.composition } : {}),
    },
  ]
}

/** Steps one line's quantity, dropping it when it reaches zero. */
export function changeQuantity(
  cart: OrderCartLine[],
  lineIndex: number,
  delta: number,
): OrderCartLine[] {
  const target = cart[lineIndex]
  // §9.374 - **`locked` no longer refuses.** The owner's example is three kofolas being poured and
  // the guest saying *"only two"*: that is a correction to an order in progress, not a refund, and
  // legacy's own replace branch names the case. A box group still refuses, because a slot of a kombo
  // is priced as part of the box rather than on its own.
  if (!target || target.boxGroupId) return cart

  return cart
    .map((line, index) =>
      index === lineIndex ? { ...line, quantity: line.quantity + delta } : line,
    )
    .filter((line) => line.quantity > 0)
}

export function removeLine(cart: OrderCartLine[], lineIndex: number): OrderCartLine[] {
  const target = cart[lineIndex]
  // §9.374 - as above: *"it also happens when something is removed from košík"*. A removal is what
  // `items` exists to express.
  if (!target || target.boxGroupId) return cart
  return cart.filter((_line, index) => index !== lineIndex)
}

/** A box is assembled as a unit, so it is removed as one. */
/**
 * Add an assembled box as one group.
 *
 * Every line carries the same `boxGroupId`, which is what lets the cart remove the box as a unit and
 * what stops `addProduct` merging a box line into a loose one of the same product — the merge predicate
 * already excludes any line with a `boxGroupId`.
 */
export function addBoxLines(
  cart: OrderCartLine[],
  groupId: string,
  groupName: string,
  lines: readonly { slotLabel: string; product: AddableProduct }[],
): OrderCartLine[] {
  if (lines.length === 0) return cart
  return [
    ...cart,
    ...lines.map((line) => ({
      productId: line.product.id,
      productName: line.product.name,
      unitPrice: line.product.price,
      quantity: 1,
      ...(line.product.categoryName ? { categoryName: line.product.categoryName } : {}),
      boxGroupId: groupId,
      boxGroupName: groupName,
      boxSlotLabel: line.slotLabel,
    })),
  ]
}

export function removeBoxGroup(cart: OrderCartLine[], boxGroupId: string): OrderCartLine[] {
  return cart.filter((line) => line.boxGroupId !== boxGroupId)
}

/**
 * Replaces one line's toppings.
 *
 * The surcharge is folded into `unitPrice` — the API has nowhere else to put
 * it — so the previous one has to come off first or repeated edits accumulate.
 * `extrasPriceCents` is what remembers it.
 */
export function setLineExtras(
  cart: OrderCartLine[],
  lineIndex: number,
  extras: string,
  extrasPriceCents: number,
  chosen: ReadonlyArray<{ ingredientId: number; quantity: number }> = [],
): OrderCartLine[] {
  return cart.map((line, index) => {
    if (index !== lineIndex) return line

    const baseCents = parseMoneyToCents(line.unitPrice) - (line.extrasPriceCents ?? 0)
    return {
      ...line,
      pizzaExtras: extras || undefined,
      extrasPriceCents: extrasPriceCents || undefined,
      // §10A.80 — the ids travel so the server can price them. The default is empty rather than
      // required, so the existing callers and their tests keep their meaning.
      extrasChosen: chosen.length > 0 ? chosen : undefined,
      unitPrice: (baseCents + extrasPriceCents) / 100,
    }
  })
}

/** How many of a product the cart holds, across every line that carries it. */
export function quantityOf(cart: OrderCartLine[], productId: number): number {
  return cart
    .filter((line) => line.productId === productId)
    .reduce((total, line) => total + line.quantity, 0)
}

/**
 * How many things are in the basket - the owner's item P3, §9.312.
 *
 * The owner asked for *"Košík (xx products)"*. **The sum of the quantities, not the number of lines**,
 * and the difference is the whole reason this is a function: three Margheritas on one line are three
 * products to whoever is reading the header and one line to the code. Legacy's own header counts
 * `cart.length` (`order-cart.tsx:83`), which would say *1* for a basket holding three pizzas.
 */
export function basketCount(cart: readonly OrderCartLine[]): number {
  return cart.reduce((total, line) => total + (line.quantity ?? 0), 0)
}

/**
 * `6 položiek`, with the plural Slovak actually uses.
 *
 * **One, two-to-four and five-or-more are three different words.** Legacy writes `položiek`
 * unconditionally, so its header reads *1 položiek* on a basket of one - wrong in a way the owner
 * reads every day. The rule is the standard Slovak one and is applied here rather than in the
 * component so it can be tested without rendering.
 */
export function basketCountLabel(count: number): string {
  if (count === 1) return `${count} ${tt('položka')}`
  if (count >= 2 && count <= 4) return `${count} ${tt('položky')}`
  return `${count} ${tt('položiek')}`
}
